'use client';
import React, { useState, useEffect } from 'react';
import { WeatherData } from '@/lib/types';
import { cyrCRunways } from '@/lib/runwayData';
import { getAllRunwayAnalyses, selectRecommendedRunway } from '@/lib/runwayWindCalculator';
import RunwayPanel from '@/components/RunwayPanel';

interface Props {
  weatherData: WeatherData;
}

function CrosswindBadge({ value }: { value: number }) {
  if (value > 15) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-700 text-ifr bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        {value} kt
      </span>
    );
  }
  if (value > 10) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-700 text-caution bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        {value} kt
      </span>
    );
  }
  return <span className="text-xs font-700 text-vfr">{value} kt</span>;
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function PistesVentContent({ weatherData }: Props) {
  const { metar } = weatherData;
  const analyses = getAllRunwayAnalyses(metar.windDirection, metar.windSpeed, cyrCRunways);
  const recommendedRunway = selectRecommendedRunway(metar.windDirection, metar.windSpeed, cyrCRunways);

  const allEnds = analyses.flatMap((a) => [
    { key: `end-${a.components.runwayEnd}`, ...a.components },
    { key: `end-${a.oppositeComponents.runwayEnd}`, ...a.oppositeComponents },
  ]);

  return (
    <div className="h-full page-scroll px-3 py-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-700 text-foreground">Pistes / Vent — CYRC</h2>
          <p className="text-sm text-muted-foreground">Composantes de vent par piste</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="card-aviation px-3 py-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <div>
              <div className="text-xs text-muted-foreground">Vent actuel</div>
              <div className="text-sm font-700 font-tabular text-foreground">
                {metar.windDirection}° / {metar.windSpeed} kt
                {metar.gustSpeed > 0 && ` raf. ${metar.gustSpeed} kt`}
              </div>
            </div>
          </div>
          <div className="card-aviation px-3 py-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-vfr" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <div>
              <div className="text-xs text-muted-foreground">Piste recommandée</div>
              <div className="text-lg font-800 text-vfr">{recommendedRunway}</div>
              {recommendedRunway === '30' && (
                <div className="text-xs text-green-700 font-700">Principale si traversier ≤ 5 kt</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main layout: diagram left, cards right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-4">
        {/* Airport diagram */}
        <div className="xl:col-span-2 card-aviation p-4 flex flex-col items-center">
          <div className="metric-label mb-3">PLAN DE L&apos;AÉROPORT — CYRC</div>
          <div className="w-full flex justify-center items-center" style={{ minHeight: '280px' }}>
            <RunwayPanel
              analyses={analyses}
              recommendedRunway={recommendedRunway}
              windDirection={metar.windDirection}
              windSpeed={metar.windSpeed}
            />
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1.5 bg-vfr rounded" />
              <span className="text-xs text-muted-foreground">Piste recommandée</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1.5 bg-muted rounded" />
              <span className="text-xs text-muted-foreground">Autre piste</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 bg-primary" />
              <span className="text-xs text-muted-foreground">Vent</span>
            </div>
          </div>
          <div className="mt-3 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 leading-snug w-full">
            À CYRC, la piste 30 est généralement utilisée lorsque le vent traversier est faible. Le pilote peut demander une autre piste au contrôleur si 24, 06 ou une autre extrémité est plus adaptée à son aéronef, ULM ou performance.
          </div>
        </div>

        {/* Runway end cards */}
        <div className="xl:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          {allEnds.map((end) => (
            <div
              key={end.key}
              className={`card-aviation p-4 flex flex-col gap-3 transition-all duration-150 ${
                end.isRecommended ? 'border-2 border-vfr bg-green-50/50' : ''
              }`}
            >
              {/* Runway number */}
              <div className="flex items-center justify-between">
                <div className={`text-3xl font-800 font-tabular ${end.isRecommended ? 'text-vfr' : 'text-foreground'}`}>
                  {end.runwayEnd}
                </div>
                {end.isRecommended && (
                  <span className="text-xs font-700 text-vfr bg-green-100 px-2 py-0.5 rounded-full">
                    {end.runwayEnd === '30' ? 'Principale' : 'Suggérée'}
                  </span>
                )}
              </div>

              {/* Wind components */}
              <div className="space-y-2">
                {/* Headwind */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-vfr" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3l-7 9h4v9h6v-9h4z" />
                    </svg>
                    <span className="text-xs text-muted-foreground font-600">Face</span>
                  </div>
                  <span className={`text-sm font-800 font-tabular ${end.headwind > 0 ? 'text-vfr' : 'text-muted-foreground'}`}>{end.headwind} kt</span>
                </div>

                {/* Tailwind */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21l7-9h-4V3H9v9H5z" />
                    </svg>
                    <span className="text-xs text-muted-foreground font-600">Arr.</span>
                  </div>
                  <span className={`text-sm font-800 font-tabular ${end.tailwind > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {end.tailwind} kt
                  </span>
                </div>

                {/* Crosswind */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M21 12l-9-7v4H3v6h9v4z" />
                    </svg>
                    <span className="text-xs text-muted-foreground font-600">Trav.</span>
                  </div>
                  <CrosswindBadge value={end.crosswind} />
                </div>
              </div>

              {/* Crosswind bar */}
              <div className="w-full bg-border rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    end.crosswind > 15 ? 'bg-ifr' :
                    end.crosswind > 10 ? 'bg-caution' : 'bg-vfr'
                  }`}
                  style={{ width: `${Math.min(100, (end.crosswind / 20) * 100)}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {end.crosswind > 15 ? '⚠️ Alerte traversier' :
                 end.crosswind > 10 ? '⚠️ Prudence traversier' : '✓ Traversier acceptable'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Threshold legend */}
      <div className="mt-3 card-aviation p-3 flex flex-wrap gap-4 items-center">
        <span className="text-xs font-600 text-muted-foreground">SEUILS:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-vfr" />
          <span className="text-xs text-muted-foreground">≤10 kt — Acceptable</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-caution" />
          <span className="text-xs text-muted-foreground">&gt;10 kt — Prudence</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-ifr" />
          <span className="text-xs text-muted-foreground">&gt;15 kt — Alerte</span>
        </div>
      </div>
    </div>
  );
}